import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Info,
  LineChart as LineIcon,
  Minus,
  RefreshCw,
} from 'lucide-react';
import { Area, Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  actionMetricKeys,
  buildGbpPerformanceView,
  buildMonthlyRowsFromMetrics,
  formatGbpDelta,
  gbpMetricLabel,
  twelveMonthWindow,
  GBP_IMPRESSION_METRICS,
  type GbpDailyRow,
  type GbpKpi,
  type GbpMonthlyRow,
} from '../../../lib/reporting/socialGbp';
import { buildGoogleReviewsReputation } from '../../../lib/reporting/socialGbpReviews';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import type { DateRange } from '../../../lib/reportingDateRange';
import type { SocialGbpKeyword } from '../../../types/socialReporting';
import { cn } from '../../../lib/utils';
import { getSocialGbpPerformance, getSocialGoogleBusiness } from './api';
import { CommunityReviewsSection } from './CommunityReviewsSection';

// The two chart view controls: shape vs bar, and the report-period (daily) vs
// 12-month (monthly) granularity. Bar is the sensible default for the monthly view.
type ChartMode = 'shape' | 'bar';
type Granularity = 'daily' | 'monthly';

// Google Business tab — the lead-adjacent performance surface that replaces the
// old "not measurable for Google Business" state. Every GBP-connected client gets
// it (fitness too, not automotive-specific); a client with no GBP shows a clean
// empty state. Self-fetching (Radix unmounts inactive tabs), React-Query cached.
//
// Performance figures are a Zernio snapshot delayed 2-3 days (Google) — the caveat
// line says so. Sections: KPI cards · daily impression + action charts · search
// keywords (below) · Google reviews reputation (below).

// Performance is a snapshot with a 2-3-day delay — long staleTime, like the
// Overview/Content tabs.
const GBP_STALE_MS = 6 * 60 * 60_000;

// Colour per metric (platform-agnostic; these are Google surfaces). Impression
// splits share a cool family; actions are distinct accents.
const METRIC_COLOR: Record<string, string> = {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: 'hsl(217 91% 60%)',
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: 'hsl(258 90% 66%)',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: 'hsl(190 85% 45%)',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: 'hsl(38 92% 50%)',
  CALL_CLICKS: 'hsl(142 71% 45%)',
  BUSINESS_DIRECTION_REQUESTS: 'hsl(217 91% 60%)',
  WEBSITE_CLICKS: 'hsl(258 90% 66%)',
  BUSINESS_BOOKINGS: 'hsl(330 81% 60%)',
  BUSINESS_CONVERSATIONS: 'hsl(24 95% 53%)',
  BUSINESS_FOOD_ORDERS: 'hsl(15 79% 54%)',
  BUSINESS_FOOD_MENU_CLICKS: 'hsl(48 90% 50%)',
};

function metricColor(metric: string): string {
  return METRIC_COLOR[metric] ?? 'hsl(215 20% 55%)';
}

export function SocialGoogleBusinessTab({
  accountId,
  range,
}: {
  accountId: string;
  range: DateRange;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['social-reporting-gbp', accountId, range.from, range.to],
    queryFn: () => getSocialGoogleBusiness(accountId, range),
    enabled: accountId !== '',
    staleTime: GBP_STALE_MS,
  });

  // Chart view controls (shape/bar + daily/monthly). Switching to monthly defaults
  // to bar (the sensible default for a 12-month trend); the user can re-toggle.
  const [chartMode, setChartMode] = useState<ChartMode>('shape');
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const changeGranularity = (g: Granularity) => {
    setGranularity(g);
    setChartMode(g === 'monthly' ? 'bar' : 'shape');
  };

  // The 12-month monthly window is derived from the report period's end (stable,
  // no clock read → a stable query key). Fetched only when the monthly view is on.
  const monthlyWindow = useMemo(() => twelveMonthWindow(range.to), [range.to]);
  const monthly = useQuery({
    queryKey: ['social-reporting-gbp-monthly', accountId, monthlyWindow.from, monthlyWindow.to],
    queryFn: () => getSocialGbpPerformance(accountId, monthlyWindow),
    enabled: accountId !== '' && granularity === 'monthly' && (query.data?.connected ?? false),
    staleTime: GBP_STALE_MS,
  });

  const view = useMemo(
    () => (query.data ? buildGbpPerformanceView(query.data) : null),
    [query.data],
  );

  // Monthly chart rows (12-month rollup), computed only in the monthly view.
  const impressionMonthlyRows = useMemo(
    () =>
      monthly.data
        ? buildMonthlyRowsFromMetrics(monthly.data.metrics, GBP_IMPRESSION_METRICS, monthlyWindow)
        : [],
    [monthly.data, monthlyWindow],
  );
  const actionMonthly = useMemo(() => {
    if (!monthly.data) return { rows: [] as GbpMonthlyRow[], metrics: [] as string[] };
    const metrics = actionMetricKeys(monthly.data.metrics, monthlyWindow);
    return { rows: buildMonthlyRowsFromMetrics(monthly.data.metrics, metrics, monthlyWindow), metrics };
  }, [monthly.data, monthlyWindow]);

  // Google review reputation (period-scoped, Google-only) — reuses the community
  // reporting core. Reviews are from OUR inbox, so always current.
  const reviews = useMemo(
    () =>
      query.data
        ? buildGoogleReviewsReputation(
            query.data.reviewThreads,
            query.data.range,
            query.data.previousRange,
          )
        : null,
    [query.data],
  );

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['social-reporting-gbp', accountId, range.from, range.to] });

  if (query.isLoading) {
    return (
      <div className="py-16">
        <CenteredSpinner />
      </div>
    );
  }
  if (query.isError || !view || !query.data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-10 text-center">
        <p className="text-sm text-red-700">Couldn't load the Google Business report.</p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mt-3 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          Try again
        </button>
      </div>
    );
  }

  // No Google Business connected — a clean, unified empty state (no industry branch).
  if (!query.data.connected) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
        <p className="text-sm font-medium text-slate-600">No Google Business connected</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">
          This client doesn't have a Google Business profile connected, so there's no Google
          performance to report. Connect one from the account's onboarding to light this up.
        </p>
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="space-y-6">
      {/* Caveat: performance is a Google snapshot (2-3 day delay); reviews are current. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            Google performance figures are a snapshot — {data.dataDelay ?? 'Google data may be delayed a few days'}
            . Reviews are current.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={query.isFetching}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={cn('h-3 w-3', query.isFetching && 'animate-spin')} aria-hidden />
          {query.isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {data.failures.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Some data couldn't be loaded.</p>
            <ul className="mt-0.5 list-inside list-disc text-xs text-amber-700">
              {data.failures.map((f) => (
                <li key={f.source}>
                  {f.source}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!view.hasPerformanceData ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-400">
          No Google Business performance recorded for this period. A recently-connected profile can
          take a few days to report.
        </div>
      ) : (
        <>
          {/* KPI cards — headline impressions + lead-adjacent actions. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {view.kpis.map((kpi) => (
              <GbpKpiCard key={kpi.key} kpi={kpi} />
            ))}
          </div>

          {/* Optional actions (bookings / messages / food) — only when non-zero. */}
          {view.optionalActions.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {view.optionalActions.map((kpi) => (
                <GbpKpiCard key={kpi.key} kpi={kpi} />
              ))}
            </div>
          )}

          {/* Chart view controls — chart type + granularity, shared by both charts. */}
          <ChartControls
            mode={chartMode}
            granularity={granularity}
            onMode={setChartMode}
            onGranularity={changeGranularity}
          />

          {/* Charts — impressions (stacked splits) + actions (calls/directions/website).
              Daily = the report period; monthly = a 12-month rollup. */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ImpressionChart
              mode={chartMode}
              granularity={granularity}
              dailyRows={view.impressionRows}
              monthlyRows={impressionMonthlyRows}
              loadingMonthly={granularity === 'monthly' && monthly.isLoading}
            />
            <ActionChart
              mode={chartMode}
              granularity={granularity}
              dailyRows={view.actionChart.rows}
              dailyMetrics={view.actionChart.metrics}
              monthlyRows={actionMonthly.rows}
              monthlyMetrics={actionMonthly.metrics}
              loadingMonthly={granularity === 'monthly' && monthly.isLoading}
            />
          </div>
        </>
      )}

      {/* Search keywords — what people searched to find this business (monthly). */}
      <KeywordsSection
        keywords={view.keywords}
        monthRange={data.keywordsMonthRange}
        capped={data.keywordsCapped}
      />

      {/* Google reviews — the reputation snapshot (rating / distribution / recent).
          Responsiveness (how fast we replied) stays on the Community tab. */}
      {reviews && (
        <CommunityReviewsSection
          view={reviews}
          title="Google reviews"
          subtitle="Google reviews received this period, the ratings, and the recent reviews."
          emptyMessage="No Google reviews received for this client in the selected period."
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// search keywords (monthly)
// ---------------------------------------------------------------------------

function formatMonth(ym: string): string {
  const d = new Date(`${ym}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function KeywordsSection({
  keywords,
  monthRange,
  capped,
}: {
  keywords: SocialGbpKeyword[];
  monthRange: { startMonth: string; endMonth: string } | null;
  capped: boolean;
}) {
  const subtitle = monthRange
    ? monthRange.startMonth === monthRange.endMonth
      ? formatMonth(monthRange.startMonth)
      : `${formatMonth(monthRange.startMonth)} – ${formatMonth(monthRange.endMonth)}`
    : undefined;
  const max = Math.max(1, ...keywords.map((k) => k.impressions));

  return (
    <ChartCard title="What people searched to find you" subtitle={subtitle}>
      {keywords.length === 0 ? (
        <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
          No search-keyword data for this period. Google only reports terms above a minimum
          threshold, and a new profile can take a month or two to accumulate them.
        </p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {keywords.map((k) => (
              <li key={k.keyword} className="flex items-center gap-3 text-sm">
                <span className="w-48 shrink-0 truncate text-slate-700" title={k.keyword}>
                  {k.keyword}
                </span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-kyck-400"
                    style={{ width: `${(k.impressions / max) * 100}%` }}
                  />
                </div>
                <span className="w-16 shrink-0 text-right tabular-nums text-slate-600">
                  {k.impressions.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
          {capped && (
            <p className="mt-2 text-xs text-slate-400">
              Google returns only the top search terms and excludes low-volume ones, so this list
              isn't exhaustive.
            </p>
          )}
        </>
      )}
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

function GbpKpiCard({ kpi }: { kpi: GbpKpi }) {
  const label = formatGbpDelta(kpi);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-slate-900">
          {kpi.current.toLocaleString()}
        </span>
        {label && kpi.direction && <DeltaChip direction={kpi.direction} label={label} />}
      </div>
      <p className="mt-1 text-xs text-slate-400">vs {kpi.previous.toLocaleString()} last period</p>
    </div>
  );
}

function DeltaChip({
  direction,
  label,
}: {
  direction: 'up' | 'down' | 'flat';
  label: string;
}) {
  const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;
  const tone =
    direction === 'up'
      ? 'bg-emerald-50 text-emerald-700'
      : direction === 'down'
        ? 'bg-red-50 text-red-700'
        : 'bg-slate-100 text-slate-500';
  return (
    <span
      className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium', tone)}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// charts
// ---------------------------------------------------------------------------

function labelDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function labelMonth(ym: string): string {
  const d = new Date(`${ym}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// chart view controls (shared by both charts)
// ---------------------------------------------------------------------------

function ChartControls({
  mode,
  granularity,
  onMode,
  onGranularity,
}: {
  mode: ChartMode;
  granularity: Granularity;
  onMode: (m: ChartMode) => void;
  onGranularity: (g: Granularity) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Segmented<Granularity>
        value={granularity}
        onChange={onGranularity}
        options={[
          { value: 'daily', label: 'Report period' },
          { value: 'monthly', label: '12 months' },
        ]}
      />
      <Segmented<ChartMode>
        value={mode}
        onChange={onMode}
        options={[
          { value: 'shape', label: 'Line', icon: LineIcon },
          { value: 'bar', label: 'Bar', icon: BarChart3 },
        ]}
      />
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; icon?: typeof LineIcon }>;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5">
      {options.map((opt) => {
        const Icon = opt.icon;
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium',
              active ? 'bg-kyck-50 text-kyck-700' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// charts (mode: shape/bar × granularity: daily/monthly)
// ---------------------------------------------------------------------------

type ChartRow = GbpDailyRow | GbpMonthlyRow;

function ImpressionChart({
  mode,
  granularity,
  dailyRows,
  monthlyRows,
  loadingMonthly,
}: {
  mode: ChartMode;
  granularity: Granularity;
  dailyRows: GbpDailyRow[];
  monthlyRows: GbpMonthlyRow[];
  loadingMonthly: boolean;
}) {
  const config: ChartConfig = useMemo(
    () =>
      Object.fromEntries(
        GBP_IMPRESSION_METRICS.map((m) => [m, { label: gbpMetricLabel(m), color: metricColor(m) }]),
      ),
    [],
  );
  const monthly = granularity === 'monthly';
  const rows: ChartRow[] = monthly ? monthlyRows : dailyRows;
  const subtitle = monthly
    ? 'Monthly totals, split by surface — last 12 months'
    : 'Daily, split by surface';

  return (
    <ChartCard title="Search impressions" subtitle={subtitle}>
      <ChartFrame loading={loadingMonthly} empty={rows.length === 0}>
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          <ComposedChart data={rows} margin={{ left: 4, right: 8, top: 4, bottom: 0 }} accessibilityLayer>
            {chartAxes(monthly)}
            {GBP_IMPRESSION_METRICS.map((m) =>
              mode === 'bar' ? (
                <Bar
                  key={m}
                  dataKey={m}
                  name={gbpMetricLabel(m)}
                  stackId="impr"
                  fill={`var(--color-${m})`}
                  isAnimationActive={false}
                />
              ) : (
                <Area
                  key={m}
                  type="monotone"
                  dataKey={m}
                  name={gbpMetricLabel(m)}
                  stackId="impr"
                  stroke={`var(--color-${m})`}
                  fill={`var(--color-${m})`}
                  fillOpacity={0.2}
                  strokeWidth={1.5}
                  isAnimationActive={false}
                />
              ),
            )}
          </ComposedChart>
        </ChartContainer>
      </ChartFrame>
    </ChartCard>
  );
}

function ActionChart({
  mode,
  granularity,
  dailyRows,
  dailyMetrics,
  monthlyRows,
  monthlyMetrics,
  loadingMonthly,
}: {
  mode: ChartMode;
  granularity: Granularity;
  dailyRows: GbpDailyRow[];
  dailyMetrics: string[];
  monthlyRows: GbpMonthlyRow[];
  monthlyMetrics: string[];
  loadingMonthly: boolean;
}) {
  const monthly = granularity === 'monthly';
  const rows: ChartRow[] = monthly ? monthlyRows : dailyRows;
  const metrics = monthly ? monthlyMetrics : dailyMetrics;
  const config: ChartConfig = useMemo(
    () => Object.fromEntries(metrics.map((m) => [m, { label: gbpMetricLabel(m), color: metricColor(m) }])),
    [metrics],
  );
  const subtitle = monthly
    ? 'Calls, direction requests and website clicks — monthly'
    : 'Calls, direction requests and website clicks — daily';

  return (
    <ChartCard title="Customer actions" subtitle={subtitle}>
      <ChartFrame loading={loadingMonthly} empty={rows.length === 0}>
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          <ComposedChart data={rows} margin={{ left: 4, right: 8, top: 4, bottom: 0 }} accessibilityLayer>
            {chartAxes(monthly)}
            {metrics.map((m) =>
              mode === 'bar' ? (
                <Bar
                  key={m}
                  dataKey={m}
                  name={gbpMetricLabel(m)}
                  fill={`var(--color-${m})`}
                  maxBarSize={22}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={m}
                  type="monotone"
                  dataKey={m}
                  name={gbpMetricLabel(m)}
                  stroke={`var(--color-${m})`}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ),
            )}
          </ComposedChart>
        </ChartContainer>
      </ChartFrame>
    </ChartCard>
  );
}

// Shared axes + tooltip for the two charts (labels switch daily↔monthly). Returns
// an ARRAY of elements (not a wrapper component) — recharts inspects the `type` of
// its direct children to locate axes, so they must not be nested in a component;
// an array is flattened and each element's type is seen.
function chartAxes(monthly: boolean): React.ReactNode[] {
  const fmt = monthly ? labelMonth : labelDate;
  return [
    <CartesianGrid key="grid" vertical={false} strokeDasharray="3 3" />,
    <XAxis
      key="x"
      dataKey={monthly ? 'month' : 'date'}
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      minTickGap={monthly ? 8 : 28}
      tickFormatter={fmt}
    />,
    <YAxis key="y" width={36} allowDecimals={false} tickLine={false} axisLine={false} />,
    <ChartTooltip
      key="tt"
      content={<ChartTooltipContent labelFormatter={(v) => fmt(String(v))} />}
    />,
  ];
}

function ChartFrame({
  loading,
  empty,
  children,
}: {
  loading: boolean;
  empty: boolean;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md bg-slate-50">
        <CenteredSpinner />
      </div>
    );
  }
  if (empty) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md bg-slate-50 text-sm text-slate-400">
        No data to chart for this period.
      </div>
    );
  }
  return <>{children}</>;
}
